// 정책 화면 쿼리 캐시의 디스크 영속화를 인증 상태에 묶는 보이지 않는 컴포넌트.
//
// 로그인(매장 확정) 시: 디스크 캐시를 hydrate → 이후 변경을 디스크에 계속 반영.
// 로그아웃 시: 디스크 캐시 전체 비움 + 프론트 ETag 메모리 캐시 비움(매장 간 가격 잔존 방지).
// 매장(실효 tenantId)이 바뀌면 정리 후 새 매장 키로 다시 시작한다.
//
// 렌더 출력 없음(null). AuthProvider 안, 라우트 트리 바깥에 한 번만 둔다.
import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../context/AuthContext";
import {
  hydratePolicyCache,
  startPersistingPolicyCache,
  clearPersistedPolicyCache,
} from "../../lib/persistCache";
import { clearPolicyEtagCache } from "../../pages/Policy/policyApi";
import { clearUploadSession } from "../../lib/uploadSessionStore";

export default function QueryCachePersistor() {
  const { user, profile, loading, profileLoading } = useAuth();
  const qc = useQueryClient();
  // 서버가 응답을 정하는 매장과 동일하게: 전역 관리자의 '들여다보기'(viewingTenantId)를 우선한다.
  // (서버 getTenantId = viewingTenantId ?? tenantId. 프론트 키가 어긋나면 다른 매장 가격을 복원한다.)
  const tenantId = profile?.viewingTenantId ?? profile?.tenantId ?? null;
  const userId = user?.id ?? null;
  const resolvedProfile = !!profile && profile.id === userId;
  const previousUserIdRef = useRef<string | null | undefined>(undefined);
  const activeScopeRef = useRef<string | undefined>(undefined);

  // 메모리 react-query 키는 계정 ID를 포함하지 않는다. 따라서 세션 사용자 자체가 바뀌는
  // 순간 전체 메모리 캐시를 지워야, A 계정의 성공 응답을 B 계정 화면이 staleTime 동안
  // 그대로 그리지 않는다. 업로드 임시복원도 정책 원문을 담으므로 같은 경계에서 비운다.
  useEffect(() => {
    if (loading) return;
    const previousUserId = previousUserIdRef.current;
    const userChanged = previousUserId !== undefined && previousUserId !== userId;
    if (userChanged || userId === null) {
      qc.clear();
      clearPolicyEtagCache();
      clearUploadSession();
      void clearPersistedPolicyCache();
    }
    previousUserIdRef.current = userId;
  }, [loading, userId, qc]);

  useEffect(() => {
    // 새 사용자 세션은 잡혔지만 /auth/me가 아직 앞 계정 profile을 교체하지 못한 구간에는
    // 어느 tenant 키도 hydrate하지 않는다. profile.id까지 현재 user와 맞아야 스코프가 확정이다.
    if (loading || profileLoading || !userId || !resolvedProfile) return;

    const scope = `${userId}:${tenantId ?? "none"}`;
    if (activeScopeRef.current !== undefined && activeScopeRef.current !== scope) {
      // 같은 본사 계정이 '보는 매장'을 바꾸는 경우도 데이터 스코프 전환이다.
      qc.clear();
      clearPolicyEtagCache();
      clearUploadSession();
    }
    activeScopeRef.current = scope;

    let active = true;
    let stop: () => void = () => {};
    void (async () => {
      // hydrate 를 먼저 끝내고 구독을 건다 — 끌어온 직후의 캐시 변경이 곧장 디스크로 반영되게.
      await hydratePolicyCache(qc, tenantId, () => active && activeScopeRef.current === scope);
      if (active) stop = startPersistingPolicyCache(qc, tenantId);
    })();
    return () => {
      active = false;
      stop();
    };
  }, [loading, profileLoading, userId, resolvedProfile, tenantId, qc]);

  return null;
}
